// src/oura/merchant/setup/AnswerInputs.tsx
//
// The two answer boxes the Setup page's questions need beyond a plain input:
// a few short entries as chips, and offered answers to pick with room for the
// merchant's own. Controlled: what is typed and not yet added lives with the
// caller, so it can count and save it.

import { useRef, type ClipboardEvent, type KeyboardEvent } from "react";
import { CheckIcon, PlusIcon, XIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "@oura/ui";
import { addEntries, type SetupQuestion } from "@oura/onboarding/setupQuestions";

/**
 * A few short entries as chips in one box, typed in at the end. Enter or Add
 * adds one, a pasted list adds a chip per line, and Backspace on an empty box
 * takes the last one back to edit. Leaving the box keeps the words where they
 * are: turning them into a chip then would move what is being pressed.
 */
export function ListInput({
    id,
    q,
    value,
    text,
    onText,
    error,
    describedBy,
    readOnly,
    onChange,
}: {
    id: string;
    q: SetupQuestion;
    value: string[];
    text: string;
    onText: (text: string) => void;
    error: boolean;
    describedBy: string;
    readOnly: boolean;
    onChange: (value: string[]) => void;
}) {
    const input = useRef<HTMLInputElement>(null);

    const add = () => {
        if (!text.trim()) return;
        onChange(addEntries(value, text));
        onText("");
    };
    const keyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            add();
        } else if (e.key === "Backspace" && text === "" && value.length > 0) {
            e.preventDefault();
            onText(value[value.length - 1]);
            onChange(value.slice(0, -1));
        }
    };
    const paste = (e: ClipboardEvent<HTMLInputElement>) => {
        const pasted = e.clipboardData.getData("text");
        if (!/\r?\n/.test(pasted.trim())) return;
        e.preventDefault();
        // In place of what is selected, as a normal paste would be.
        const box = e.currentTarget;
        const start = box.selectionStart ?? text.length;
        const end = box.selectionEnd ?? start;
        onChange(addEntries(value, `${text.slice(0, start)}${pasted}${text.slice(end)}`));
        onText("");
    };

    return (
        <div
            onClick={(e) => e.target === e.currentTarget && input.current?.focus()}
            className={cn(
                "flex flex-wrap items-center gap-1.5 rounded-xl bg-white p-1.5 ring-1 ring-inset focus-within:ring-2 focus-within:ring-blue-600",
                error ? "ring-rose-600" : "ring-black/[0.08]",
            )}
        >
            {value.map((v, i) => (
                <span key={v} className="inline-flex min-h-8 max-w-full items-center gap-0.5 rounded-full bg-blue-50 py-1 pl-3 pr-1 text-sm text-blue-900 motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-150">
                    <span className="min-w-0 break-words">{v}</span>
                    {!readOnly && (
                        <button
                            type="button"
                            aria-label={`Remove ${v}`}
                            onClick={() => {
                                onChange(value.filter((_, j) => j !== i));
                                input.current?.focus();
                            }}
                            className={cn("grid h-6 w-6 shrink-0 place-items-center rounded-full text-blue-400 hover:bg-blue-100 hover:text-blue-800", FOCUS_RING)}
                        >
                            <XIcon aria-hidden size={13} />
                        </button>
                    )}
                </span>
            ))}
            {/* Read-only (view as the merchant) with nothing in it: say so at the
                field's own height, not a bare frame that reads as broken (#4481). */}
            {readOnly && value.length === 0 && (
                <span className="flex h-8 items-center px-2 text-base text-gray-400 sm:text-sm">Not answered yet</span>
            )}
            {!readOnly && (
                <span className="flex min-w-[9rem] flex-1 items-center">
                    <input
                        ref={input}
                        id={id}
                        value={text}
                        placeholder={value.length > 0 ? "Add another" : q.placeholder}
                        enterKeyHint="enter"
                        autoComplete="off"
                        aria-invalid={error ? true : undefined}
                        aria-describedby={describedBy}
                        onChange={(e) => onText(e.target.value)}
                        onKeyDown={keyDown}
                        onPaste={paste}
                        className="h-8 min-w-0 flex-1 bg-transparent px-2 text-base text-gray-900 outline-none placeholder:text-gray-400 sm:text-sm"
                    />
                    {text.trim() && <AddButton onAdd={add} />}
                </span>
            )}
        </div>
    );
}

/** The offered answers as toggles, the merchant's own beside them, and a box to add one. */
export function ChoiceInput({
    id,
    q,
    labelledBy,
    value,
    text,
    onText,
    describedBy,
    readOnly,
    onChange,
}: {
    id: string;
    q: SetupQuestion;
    labelledBy: string;
    value: string[];
    text: string;
    onText: (text: string) => void;
    describedBy: string;
    readOnly: boolean;
    onChange: (value: string[]) => void;
}) {
    const options = q.options ?? [];
    const has = (v: string) => value.some((x) => x.toLowerCase() === v.toLowerCase());
    const own = value.filter((v) => !options.some((o) => o.toLowerCase() === v.toLowerCase()));
    const toggle = (v: string) => onChange(has(v) ? value.filter((x) => x.toLowerCase() !== v.toLowerCase()) : [...value, v]);
    const add = () => {
        if (!text.trim()) return;
        onChange(addEntries(value, text));
        onText("");
    };

    return (
        <div role="group" aria-labelledby={labelledBy} aria-describedby={describedBy} className="flex flex-wrap items-center gap-2">
            {[...options, ...own].map((v, i) => {
                const on = has(v);
                return (
                    <button
                        key={v}
                        id={i === 0 ? id : undefined}
                        type="button"
                        aria-pressed={on}
                        disabled={readOnly}
                        onClick={() => toggle(v)}
                        className={cn(
                            "inline-flex min-h-9 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-left text-sm transition-colors",
                            on ? "bg-blue-50 font-medium text-blue-800 ring-1 ring-inset ring-blue-200" : "bg-white text-gray-700 ring-1 ring-inset ring-black/[0.08] hover:bg-gray-50",
                            FOCUS_RING,
                        )}
                    >
                        {on && <CheckIcon aria-hidden size={14} strokeWidth={2.5} />}
                        {v}
                    </button>
                );
            })}
            {!readOnly && (
                <span className="inline-flex h-9 items-center rounded-full bg-white pl-3 pr-1 ring-1 ring-inset ring-black/[0.08] focus-within:ring-2 focus-within:ring-blue-600">
                    <PlusIcon aria-hidden size={14} className="shrink-0 text-gray-400" />
                    <input
                        value={text}
                        placeholder={q.placeholder}
                        aria-label="Add your own"
                        autoComplete="off"
                        enterKeyHint="enter"
                        onChange={(e) => onText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                add();
                            }
                        }}
                        className="h-8 w-36 bg-transparent px-1.5 text-base text-gray-900 outline-none placeholder:text-gray-400 sm:text-sm"
                    />
                    {text.trim() && <AddButton onAdd={add} />}
                </span>
            )}
        </div>
    );
}

/** A tap target for phones, where Enter is easy to miss. Keeps focus in the box. */
function AddButton({ onAdd }: { onAdd: () => void }) {
    return (
        <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onAdd}
            className={cn("inline-flex h-7 shrink-0 items-center gap-1 rounded-full bg-blue-600 px-2.5 text-xs font-medium text-white", FOCUS_RING)}
        >
            <PlusIcon aria-hidden size={12} strokeWidth={2.5} />
            Add
        </button>
    );
}
